import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Storage, ref, uploadBytes, getDownloadURL, deleteObject, listAll, getMetadata } from '@angular/fire/storage';
import { DocumentSnapshot } from '@angular/fire/firestore';
import { NotificationService } from '../../services/notification.service';
import { NotificationComponent } from '../../components/notification/notification.component';
import { WebsiteService } from '../../services/website.service';
import { ImageUploadService } from '../../services/image-upload.service';
import { MediaService, MediaDocument, MediaStats } from '../../services/media.service';

interface MediaFile {
  id?: string;            // Firestore document ID
  name: string;
  url: string;
  fullPath: string;
  size: number;
  contentType: string;
  timeCreated: string;
  variants?: {
    original?: string;
    webp1400?: string;
    webp600?: string;
    webp200?: string;
  };
}

@Component({
  selector: 'app-media',
  templateUrl: './media.component.html',
  styleUrls: ['./media.component.scss'],
  standalone: true,
  imports: [CommonModule, NotificationComponent]
})
export class MediaComponent implements OnInit, OnDestroy {
  mediaFiles: MediaFile[] = [];
  loading = false;
  uploading = false;
  uploadProgress = 0;
  uploadStatus = '';
  selectedFile: File | null = null;
  showDeleteModal = false;
  fileToDelete: MediaFile | null = null;
  viewMode: 'grid' | 'list' = this.getStoredViewMode();
  showImageModal = false;
  currentImageIndex = 0;
  currentImage: MediaFile | null = null;
  imageZoom = 1;

  stats: MediaStats | null = null;
  migrating = false;
  migrationProgress = '';

  private readonly PAGE_SIZE = 48;
  currentPageIndex = 0;
  hasNextPage = false;
  hasPrevPage = false;
  private lastDoc: DocumentSnapshot | null = null;
  private pageSnapshots: (DocumentSnapshot | null)[] = [null]; // Track cursor per page

  constructor(
    private storage: Storage,
    private notificationService: NotificationService,
    private websiteService: WebsiteService,
    private imageUploadService: ImageUploadService,
    private mediaService: MediaService
  ) {}

  async ngOnInit() {
    await Promise.all([
      this.loadMediaFiles(),
      this.loadStats()
    ]);
    document.addEventListener('keydown', (event) => this.onKeyDown(event));
  }

  private getStoredViewMode(): 'grid' | 'list' {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('mediaViewMode');
      if (stored === 'grid' || stored === 'list') {
        return stored;
      }
    }
    return 'grid';
  }

  saveViewMode(mode: 'grid' | 'list') {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('mediaViewMode', mode);
    }
  }

  ngOnDestroy() {
    document.removeEventListener('keydown', (event) => this.onKeyDown(event));
  }

  async loadStats() {
    this.stats = await this.mediaService.getStats();
  }

  /**
   * Load media files from Firestore (fast, sorted newest-first).
   */
  async loadMediaFiles() {
    this.currentPageIndex = 0;
    this.pageSnapshots = [null];
    this.lastDoc = null;

    try {
      this.loading = true;
      const result = await this.mediaService.getMediaFiles(this.PAGE_SIZE);
      this.mediaFiles = result.files.map(f => this.toMediaFile(f));
      this.lastDoc = result.lastDoc;
      this.hasNextPage = result.files.length === this.PAGE_SIZE;
      this.hasPrevPage = false;
    } catch (error) {
      console.error('Error loading media files:', error);
      this.notificationService.show({
        message: 'Fout bij laden van media bestanden',
        type: 'error'
      });
    } finally {
      this.loading = false;
    }
  }

  private toMediaFile(doc: MediaDocument & { id: string }): MediaFile {
    return {
      id: doc.id,
      name: doc.name,
      url: doc.originalUrl,
      fullPath: doc.storagePath,
      size: doc.size,
      contentType: doc.contentType,
      timeCreated: doc.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      variants: (doc.webp1400Url || doc.webp600Url || doc.webp200Url) ? {
        original: doc.originalUrl,
        webp1400: doc.webp1400Url,
        webp600: doc.webp600Url,
        webp200: doc.webp200Url
      } : undefined
    };
  }

  async nextPage() {
    if (!this.hasNextPage || !this.lastDoc) return;
    this.loading = true;
    try {
      this.currentPageIndex++;
      // Save cursor for going back
      if (this.pageSnapshots.length <= this.currentPageIndex) {
        this.pageSnapshots.push(this.lastDoc);
      }
      const result = await this.mediaService.getMediaFiles(this.PAGE_SIZE, this.lastDoc);
      this.mediaFiles = result.files.map(f => this.toMediaFile(f));
      this.lastDoc = result.lastDoc;
      this.hasNextPage = result.files.length === this.PAGE_SIZE;
      this.hasPrevPage = this.currentPageIndex > 0;
    } catch (error) {
      console.error('Error loading next page:', error);
      this.currentPageIndex--;
    } finally {
      this.loading = false;
    }
  }

  async prevPage() {
    if (this.currentPageIndex <= 0) return;
    this.currentPageIndex--;
    // Reload from the saved cursor for this page
    this.loading = true;
    try {
      const cursor = this.pageSnapshots[this.currentPageIndex] || undefined;
      const result = await this.mediaService.getMediaFiles(this.PAGE_SIZE, cursor);
      this.mediaFiles = result.files.map(f => this.toMediaFile(f));
      this.lastDoc = result.lastDoc;
      this.hasNextPage = result.files.length === this.PAGE_SIZE;
      this.hasPrevPage = this.currentPageIndex > 0;
    } catch (error) {
      console.error('Error loading previous page:', error);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Migrate existing Storage files to Firestore index (one-time operation).
   */
  async migrateExistingFiles() {
    if (this.migrating) return;
    this.migrating = true;
    this.migrationProgress = 'Bestanden ophalen uit Storage...';

    try {
      const mediaRef = ref(this.storage, 'media');
      const result = await listAll(mediaRef);

      // Filter originals and collect variant URLs
      const originals = result.items.filter(item => {
        const n = item.name;
        return !n.includes('-webp1400') && !n.includes('-webp600') && !n.includes('-webp200');
      });

      this.migrationProgress = `${originals.length} bestanden gevonden, metadata ophalen...`;

      const CONCURRENCY = 10;
      const storageFiles: { name: string; url: string; fullPath: string; size: number; contentType: string; timeCreated: string; variants?: { original?: string; webp1400?: string; webp600?: string; webp200?: string } }[] = [];

      for (let i = 0; i < originals.length; i += CONCURRENCY) {
        const batch = originals.slice(i, i + CONCURRENCY);
        const results = await Promise.all(batch.map(async (itemRef) => {
          try {
            const [url, metadata] = await Promise.all([getDownloadURL(itemRef), getMetadata(itemRef)]);
            const match = itemRef.name.match(/^(\d+)-(.+?)(?:-(original))?(\.[^.]+)?$/);
            let variants: { original?: string; webp1400?: string; webp600?: string; webp200?: string } | undefined;
            if (match) {
              const ts = match[1];
              const base = match[2];
              const variantUrls: Record<string, string> = {};
              await Promise.all(['webp1400', 'webp600', 'webp200'].map(async (v) => {
                try {
                  const vRef = ref(this.storage, `media/${ts}-${base}-${v}.webp`);
                  variantUrls[v] = await getDownloadURL(vRef);
                } catch { /* no variant */ }
              }));
              if (Object.keys(variantUrls).length > 0) {
                variants = { original: url, ...variantUrls };
              }
            }
            return { name: itemRef.name, url, fullPath: itemRef.fullPath, size: metadata.size, contentType: metadata.contentType || 'unknown', timeCreated: metadata.timeCreated || new Date().toISOString(), variants };
          } catch { return null; }
        }));
        results.forEach(r => { if (r) storageFiles.push(r); });
        this.migrationProgress = `Metadata ophalen: ${Math.min(i + CONCURRENCY, originals.length)}/${originals.length}`;
      }

      this.migrationProgress = 'Migreren naar Firestore...';
      const count = await this.mediaService.migrateFromStorage(storageFiles, (current, total) => {
        this.migrationProgress = `Migreren: ${current}/${total}`;
      });

      this.migrationProgress = '';
      this.notificationService.show({ message: `${count} bestanden gemigreerd`, type: 'success' });

      // Reload
      await Promise.all([this.loadMediaFiles(), this.loadStats()]);
    } catch (error) {
      console.error('Migration failed:', error);
      this.notificationService.show({ message: 'Migratie mislukt', type: 'error' });
    } finally {
      this.migrating = false;
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
    }
  }

  async uploadFile() {
    if (!this.selectedFile) {
      this.notificationService.show({
        message: 'Selecteer eerst een bestand',
        type: 'error'
      });
      return;
    }

    try {
      this.uploading = true;
      this.uploadProgress = 0;
      this.uploadStatus = 'Uploaden...';
      
      const timestamp = Date.now();
      const baseFileName = this.selectedFile.name.replace(/\.[^/.]+$/, ''); // Remove extension
      
      // Check if it's an image
      const isImage = this.selectedFile.type.startsWith('image/');
      
      if (isImage) {
        // Process image: create original and WebP variants via central service
        // ImageUploadService also registers in Firestore automatically
        this.uploadStatus = 'Afbeelding verwerken...';
        const variants = await this.imageUploadService.uploadImageWithVariants(this.selectedFile, (progress) => {
          this.uploadStatus = progress.status;
          this.uploadProgress = progress.progress;
        });

        // Prepend to current view
        const originalRefName = `${timestamp}-${baseFileName}-original`;
        const newFile: MediaFile = {
          name: originalRefName,
          url: variants.original,
          fullPath: `media/${originalRefName}`,
          size: this.selectedFile.size,
          contentType: this.selectedFile.type,
          timeCreated: new Date().toISOString(),
          variants: {
            original: variants.original,
            webp1400: variants.webp1400,
            webp600: variants.webp600,
            webp200: variants.webp200
          }
        };
        this.mediaFiles = [newFile, ...this.mediaFiles];
      } else {
        // Upload non-image files as-is
        this.uploadStatus = 'Bestand uploaden...';
        this.uploadProgress = 50;
        const fileName = `${timestamp}-${this.selectedFile.name}`;
        const storageRef = ref(this.storage, `media/${fileName}`);
        await uploadBytes(storageRef, this.selectedFile);
        const fileUrl = await getDownloadURL(storageRef);
        this.uploadProgress = 90;

        // Register in Firestore
        await this.mediaService.addMediaDocument({
          name: fileName,
          originalUrl: fileUrl,
          storagePath: `media/${fileName}`,
          size: this.selectedFile.size,
          contentType: this.selectedFile.type || 'application/octet-stream'
        });
        this.uploadProgress = 100;

        // Prepend to current view
        const newFile: MediaFile = {
          name: fileName,
          url: fileUrl,
          fullPath: `media/${fileName}`,
          size: this.selectedFile.size,
          contentType: this.selectedFile.type || 'application/octet-stream',
          timeCreated: new Date().toISOString()
        };
        this.mediaFiles = [newFile, ...this.mediaFiles];
      }

      // Refresh stats
      this.loadStats();

      this.uploadStatus = 'Voltooid!';
      this.notificationService.show({
        message: 'Bestand succesvol geüpload',
        type: 'success'
      });

      // Reset file input
      this.selectedFile = null;
      const fileInput = document.getElementById('fileInput') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      
      // Reset progress after a short delay
      setTimeout(() => {
        this.uploadProgress = 0;
        this.uploadStatus = '';
      }, 2000);
    } catch (error) {
      console.error('Error uploading file:', error);
      this.uploadStatus = 'Fout opgetreden';
      this.notificationService.show({
        message: 'Fout bij uploaden van bestand',
        type: 'error'
      });
    } finally {
      this.uploading = false;
    }
  }

  async deleteFile(file: MediaFile) {
    try {
      // Delete original from Storage (ignore if already gone)
      try {
        const fileRef = ref(this.storage, file.fullPath);
        await deleteObject(fileRef);
      } catch (e: any) {
        if (e?.code !== 'storage/object-not-found') {
          console.warn('Could not delete original from storage:', e);
        }
      }

      // Also try to delete variants from Storage
      const match = file.name.match(/^(\d+)-(.+?)(?:-(original))?(\.[^.]+)?$/);
      if (match) {
        const ts = match[1];
        const base = match[2];
        for (const variant of ['webp1400', 'webp600', 'webp200']) {
          try {
            await deleteObject(ref(this.storage, `media/${ts}-${base}-${variant}.webp`));
          } catch { /* variant may not exist */ }
        }
      }

      // Delete from Firestore + update stats
      if (file.id) {
        await this.mediaService.deleteMediaDocument(file.id);
      }

      this.notificationService.show({
        message: 'Bestand succesvol verwijderd',
        type: 'success'
      });

      this.showDeleteModal = false;
      this.fileToDelete = null;

      // Remove from current view and refresh stats
      this.mediaFiles = this.mediaFiles.filter(f => f.fullPath !== file.fullPath);
      this.loadStats();
    } catch (error) {
      console.error('Error deleting file:', error);
      this.notificationService.show({
        message: 'Fout bij verwijderen van bestand',
        type: 'error'
      });
    }
  }

  openDeleteModal(file: MediaFile) {
    this.fileToDelete = file;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.fileToDelete = null;
  }

  copyUrl(url: string) {
    navigator.clipboard.writeText(url).then(() => {
      this.notificationService.show({
        message: 'URL gekopieerd naar klembord',
        type: 'success'
      });
    }).catch(() => {
      this.notificationService.show({
        message: 'Fout bij kopiëren van URL',
        type: 'error'
      });
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  isImage(contentType: string): boolean {
    return contentType.startsWith('image/');
  }

  isVideo(contentType: string): boolean {
    return contentType.startsWith('video/');
  }

  isAudio(contentType: string): boolean {
    return contentType.startsWith('audio/');
  }

  getImageFiles(): MediaFile[] {
    return this.mediaFiles.filter(file => this.isImage(file.contentType));
  }

  toggleViewMode() {
    this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
    this.saveViewMode(this.viewMode);
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'Onbekend';
    try {
      return new Date(dateString).toLocaleString('nl-NL');
    } catch {
      return 'Onbekend';
    }
  }

  openImageModal(file: MediaFile) {
    const imageFiles = this.getImageFiles();
    const index = imageFiles.findIndex(f => f.fullPath === file.fullPath);
    if (index !== -1) {
      this.currentImageIndex = index;
      this.currentImage = imageFiles[index];
      this.imageZoom = 1;
      this.showImageModal = true;
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
  }

  closeImageModal() {
    this.showImageModal = false;
    this.currentImage = null;
    this.imageZoom = 1;
    // Restore body scroll
    document.body.style.overflow = '';
  }

  nextImage() {
    const imageFiles = this.getImageFiles();
    if (imageFiles.length === 0) return;
    this.currentImageIndex = (this.currentImageIndex + 1) % imageFiles.length;
    this.currentImage = imageFiles[this.currentImageIndex];
    this.imageZoom = 1;
  }

  previousImage() {
    const imageFiles = this.getImageFiles();
    if (imageFiles.length === 0) return;
    this.currentImageIndex = (this.currentImageIndex - 1 + imageFiles.length) % imageFiles.length;
    this.currentImage = imageFiles[this.currentImageIndex];
    this.imageZoom = 1;
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.showImageModal) return;
    
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        this.nextImage();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.previousImage();
        break;
      case 'Escape':
        event.preventDefault();
        this.closeImageModal();
        break;
      case '+':
      case '=':
        event.preventDefault();
        this.zoomIn();
        break;
      case '-':
        event.preventDefault();
        this.zoomOut();
        break;
      case '0':
        event.preventDefault();
        this.resetZoom();
        break;
    }
  }

  zoomIn() {
    this.imageZoom = Math.min(this.imageZoom + 0.25, 3);
  }

  zoomOut() {
    this.imageZoom = Math.max(this.imageZoom - 0.25, 0.5);
  }

  resetZoom() {
    this.imageZoom = 1;
  }

  get Math() {
    return Math;
  }

  onVariantChange(event: Event, file: MediaFile) {
    const select = event.target as HTMLSelectElement;
    const urlInput = document.getElementById('url-input-' + file.fullPath) as HTMLInputElement;
    if (urlInput) {
      urlInput.value = select.value;
    }
  }
}
